import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Paper,
  Typography,
  Divider,
  Button,
  IconButton,
  Box,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { ConsultationGuideData } from "../GuideDetailsDialog";
import { OrganizationAffiliation } from "fhir/r5";
import { useState } from "react";

interface ProcedureSectionProps {
  formData: ConsultationGuideData;
  onFieldChange: (name: string, value: any) => void;
  affiliation: OrganizationAffiliation | null;
}

export const ProcedureSection = ({
  formData,
  onFieldChange,
  affiliation,
}: ProcedureSectionProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleProcedureChange = (index: number, field: string, value: any) => {
    const updatedProcedimentos = [...formData.procedimentos];
    updatedProcedimentos[index] = {
      ...updatedProcedimentos[index],
      [field]: value,
    };
    onFieldChange("procedimentos", updatedProcedimentos);
  };

  const options = affiliation?.specialty?.map((specialty) => ({
    code: specialty.coding?.[0]?.code,
    display: specialty.coding?.[0]?.display,
    value: specialty?.extension?.[0]?.valueMoney?.value,
    system: specialty.coding?.[0]?.system,
  }));

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddProcedure = (option: {
    code: string | undefined;
    display: string | undefined;
    value: number | undefined;
    system: string | undefined;
  }) => {
    const newProcedimento = {
      codigoTabelaReferencia: option.system?.includes(
        "TUSSProcedimentosEventos"
      )
        ? "22"
        : "Tabela Própria das O",
      codigoProcedimento: option.code || "",
      descricaoProcedimento: option.display || "",
      valorProcedimento: option.value?.toFixed(2) || "0.00",
      sequencialItem: formData.procedimentos.length + 1,
    };
    onFieldChange("procedimentos", [
      ...formData.procedimentos,
      newProcedimento,
    ]);
    handleMenuClose();
  };

  const handleRemoveProcedure = (index: number) => {
    const updatedProcedimentos = formData.procedimentos.filter(
      (_, i) => i !== index
    );
    // Atualiza os sequencialItem após a remoção
    const reorderedProcedimentos = updatedProcedimentos.map((proc, i) => ({
      ...proc,
      sequencialItem: i + 1,
    }));
    onFieldChange("procedimentos", reorderedProcedimentos);
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Procedimentos</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleMenuOpen}
          size="small"
          disabled={!options?.length}
        >
          Incluir Procedimento
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {options?.length ? (
          <List sx={{ width: 300, maxHeight: 400, overflow: "auto" }}>
            {options.map((option, index) => (
              <ListItemButton
                key={index}
                onClick={() => handleAddProcedure(option)}
                divider={index < options.length - 1}
              >
                <ListItemText
                  primary={option.display || "Sem descrição"}
                  secondary={`Valor: R$${option.value || "N/A"}`}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box p={2}>
            <Typography color="textSecondary">
              Nenhum procedimento disponível
            </Typography>
          </Box>
        )}
      </Popover>

      {formData.procedimentos.map((procedimento, index) => (
        <Box key={index} mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Tabela</InputLabel>
                <Select
                  value={procedimento.codigoTabelaReferencia}
                  label="Tabela"
                  onChange={(e) =>
                    handleProcedureChange(
                      index,
                      "codigoTabelaReferencia",
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="Tabela Própria das O">
                    Tabela Própria das Operadoras
                  </MenuItem>
                  <MenuItem value="22">22 - Rol de procedimentos ANS</MenuItem>
                </Select>
                <FormHelperText>Tabela utilizada</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                required
                fullWidth
                label="Código"
                value={procedimento.codigoProcedimento}
                onChange={(e) =>
                  handleProcedureChange(
                    index,
                    "codigoProcedimento",
                    e.target.value
                  )
                }
                size="small"
                helperText="Código do procedimento"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Descrição"
                value={procedimento.descricaoProcedimento}
                onChange={(e) =>
                  handleProcedureChange(
                    index,
                    "descricaoProcedimento",
                    e.target.value
                  )
                }
                size="small"
                helperText="Descrição do procedimento"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                required
                fullWidth
                label="Valor do procedimento"
                type="number"
                value={procedimento.valorProcedimento}
                onChange={(e) =>
                  handleProcedureChange(
                    index,
                    "valorProcedimento",
                    e.target.value
                  )
                }
                size="small"
                helperText="Valor do procedimento"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton
                color="error"
                onClick={() => handleRemoveProcedure(index)}
                size="small"
                sx={{ p: 0, mt: -3 }}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
          {index < formData.procedimentos.length - 1 && (
            <Divider sx={{ my: 2 }} />
          )}
        </Box>
      ))}

      {formData.procedimentos.length === 0 && (
        <Typography color="textSecondary" align="center">
          Nenhum procedimento adicionado. Clique no botão acima para adicionar.
        </Typography>
      )}
    </Paper>
  );
};
